import type { RaVerificationEvent } from '@felix/domain';
import { Button, Modal, Radio, Select, Textarea, TextInput } from '@mantine/core';
import {
  IconAlertTriangle,
  IconArrowLeft,
  IconChecklist,
  IconHandStop,
  IconHourglass,
  IconPencil,
  IconPlayerPlay,
  IconPlus,
  IconPrinter,
  IconSearch,
  IconShieldCheck,
  IconUsersGroup,
} from '@tabler/icons-react';
import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../../EmptyState.js';
import { formatDateTime } from '../../../format.js';
import { DateTimeInput } from '../../../inputs/DateTimeInput.js';
import { fromZonedInput, offsetLabel, zonedInput } from '../../../inputs/lt-utc.js';
import { PageHeader } from '../../../PageHeader.js';
import { Panel } from '../../../Panel.js';
import { StatTile } from '../../../StatTile.js';
import { StatusBadge } from '../../../StatusBadge.js';
import { useShell } from '../../../shell-context.js';
import table from '../../materials/Materials.module.css';
import { browserPdfOutput, pdfErrorKey, renderPdf } from '../../personnel/pdf.js';
import { errorCode } from '../../personnel/source.js';
import purchasing from '../../purchasing/Purchasing.module.css';
import { LoadError, NoMatch, TablePager, TableSkeleton } from '../../purchasing/table.js';
import { ReturnedBanner } from '../../returned/Returned.js';
import { findingTone, raFindingsPath } from '../findings/view.js';
import library from '../library/RaLibrary.module.css';
import master from '../master/RaMaster.module.css';
import { raPdfFileName, raVerificationPdf } from '../pdf.js';
import risk from '../Risk.module.css';
import { ActionDialog, LevelBadge } from '../RiskParts.js';
import classes from './RaVerification.module.css';
import type {
  RaVerificationCandidate,
  RaVerificationDetail,
  RaVerificationHeaderInput,
  RaVerificationItem,
  RaVerificationListView,
  RaVerificationSource,
  RaVerificationSummary,
} from './types.js';
import {
  type AnswerDraft,
  changedAnswers,
  draftOf,
  EMPTY_VERIFICATION_FILTER,
  filterVerifications,
  groupItems,
  VERIFICATION_FILTERS,
  type VerificationListFilter,
  verificationStats,
  verificationTone,
} from './view.js';

const PAGE_SIZE = 15;

type Notice = { tone: 'success' | 'danger'; text: string };

export interface RaVerificationScreenProps {
  source: RaVerificationSource;
  /** Açılışta gösterilecek doğrulama (`?id=…`; bulgudan gelinir). */
  initialId?: string | null;
  /** Yazmadan sonra (gemide pano tazelenir). */
  onChanged?: () => void;
}

/** Hata kodunun metni: doğrulama metinleri, yoksa risk metinleri. */
function useVerificationError() {
  const { t } = useTranslation();
  return (error: unknown) => {
    const code = typeof error === 'string' ? error : errorCode(error);
    // PDF hatası (ADR 0095 §2): gemide PDF uygulaması yok ya da açılır pencere engellendi
    if (code.startsWith('restHours.pdf.')) return t(code);
    return (
      t(`riskVerification.errors.${code}`, { defaultValue: '' }) ||
      t(`riskDashboard.errors.${code}`, { defaultValue: '' }) ||
      t(`risk.errors.${code}`, { defaultValue: '' }) ||
      `${t('riskVerification.errors.GENERIC')} (${code})`
    );
  };
}

function useK() {
  const { t } = useTranslation();
  return {
    t,
    k: (key: string, options: Record<string, unknown> = {}) =>
      t(`riskVerification.${key}`, options),
  };
}

/** Gemi saatiyle an ve fark (UTC asıl; R26). */
function shipTime(iso: string | null | undefined, offsetMin: number): string {
  if (!iso) return '—';
  return `${formatDateTime(iso, offsetMin)} (${offsetLabel(offsetMin)})`;
}

/** Durum olayının ekran işlemi: düğme, pencere ve gerekçe. */
const ACTIONS: readonly {
  kind: RaVerificationEvent;
  can: keyof RaVerificationDetail['can'];
  key: string;
  reason: 'required' | 'optional' | 'none';
  password: boolean;
  danger?: boolean;
}[] = [
  { kind: 'submitted', can: 'submit', key: 'submit', reason: 'none', password: false },
  { kind: 'authorized', can: 'authorize', key: 'authorize', reason: 'optional', password: true },
  { kind: 'returned', can: 'return', key: 'return', reason: 'required', password: true },
  {
    kind: 'rejected',
    can: 'return',
    key: 'reject',
    reason: 'required',
    password: true,
    danger: true,
  },
  { kind: 'started', can: 'start', key: 'start', reason: 'none', password: false },
  {
    kind: 'conditions_changed',
    can: 'conditions',
    key: 'conditions',
    reason: 'required',
    password: false,
  },
  {
    kind: 'reverification_started',
    can: 'reverify',
    key: 'reverify',
    reason: 'none',
    password: false,
  },
  { kind: 'completed', can: 'complete', key: 'complete', reason: 'required', password: false },
  { kind: 'closed', can: 'close', key: 'close', reason: 'none', password: false },
  {
    kind: 'cancelled',
    can: 'cancel',
    key: 'cancel',
    reason: 'required',
    password: false,
    danger: true,
  },
  { kind: 'briefed', can: 'brief', key: 'brief', reason: 'none', password: false },
  { kind: 'stopped', can: 'stop', key: 'stop', reason: 'required', password: false, danger: true },
];

/**
 * Compliance ▸ Risk Assessment ▸ Operational Verification (K5; ADR 0089; FD §9, §11; SS §5–§8).
 * Gemide planlanan operasyon atanmış güncel şirket RA sürümüyle doğrulanır: altı ortak madde,
 * faaliyete özel maddeler ve zorunlu kontroller cevaplanır, gönderilir; altı "Yes" yetki değildir
 * (Pending Authorization). Kaptan ya da yetkili zabit yetki verir, iade eder ya da reddeder;
 * başlama, koşul değişikliği, iş durdurma, yeniden doğrulama, tamamlama ve kapanış kaydedilir.
 * Ofiste gemi seçilir, salt okunur. Tasarım yok; tablo dili Vessel Risk Register.
 */
export function RaVerificationScreen({ source, onChanged, initialId }: RaVerificationScreenProps) {
  const { k } = useK();
  const message = useVerificationError();
  // Panodan, zilden ve raporlardan gelen bağlantı: `?vessel=…&id=…` (ADR 0095 §1)
  const search = new URLSearchParams(useShell()?.search ?? '');
  // Kimlik `initialId` ile geldiyse (bulgudan; K6) o açar; yoksa panodan gelen `?id=`
  const [linked, setLinked] = useState(() => (initialId ? null : search.get('id')));
  const [state, setState] = useState<
    | { kind: 'loading' }
    | { kind: 'error'; code: string }
    | { kind: 'ready'; view: RaVerificationListView }
  >({ kind: 'loading' });
  const [vesselId, setVesselId] = useState<string | undefined>(
    () => search.get('vessel') ?? undefined,
  );
  const [opened, setOpened] = useState<RaVerificationDetail | null>(null);
  const [creating, setCreating] = useState(false);
  const [notice, setNotice] = useState<Notice | null>(null);

  const load = useCallback(async () => {
    try {
      setState({ kind: 'ready', view: await source.list(vesselId) });
    } catch (error) {
      setState({ kind: 'error', code: errorCode(error) });
    }
  }, [source, vesselId]);
  useEffect(() => {
    void load();
  }, [load]);

  const view = state.kind === 'ready' ? state.view : null;
  const open = async (id: string) => {
    try {
      setNotice(null);
      setOpened(await source.detail(id));
    } catch (error) {
      setNotice({ tone: 'danger', text: message(error) });
    }
  };
  // Bulgudan gelinince doğrulama açık gelir (K6; ADR 0094)
  // biome-ignore lint/correctness/useExhaustiveDependencies: yalnız ilk kimlikte
  useEffect(() => {
    if (initialId) void open(initialId);
  }, [initialId]);
  // biome-ignore lint/correctness/useExhaustiveDependencies: bağlantı bir kez açılır
  useEffect(() => {
    if (!linked || !view) return;
    setLinked(null);
    if (view.verifications.some((v) => v.id === linked)) void open(linked);
  }, [linked, view]);
  const changed = (next: RaVerificationDetail, text: string | null) => {
    setOpened(next);
    if (text) setNotice({ tone: 'success', text });
    void load();
    onChanged?.();
  };

  return (
    <div className={purchasing.screen}>
      <PageHeader
        title={k('title')}
        description={k('description')}
        icon={<IconChecklist size={26} />}
        actions={
          !opened && source.create && view?.can.create ? (
            <Button leftSection={<IconPlus size={16} />} onClick={() => setCreating(true)}>
              {k('new')}
            </Button>
          ) : null
        }
      />
      <p className={master.note}>{source.scope === 'ship' ? k('shipNote') : k('officeNote')}</p>
      {notice ? (
        <p
          className={purchasing.notice}
          data-tone={notice.tone}
          role={notice.tone === 'danger' ? 'alert' : 'status'}
        >
          {notice.text}
        </p>
      ) : null}
      {state.kind === 'error' ? (
        <Panel>
          <LoadError title={k('empty.error')} code={state.code} onRetry={() => void load()} />
        </Panel>
      ) : !view ? (
        <Panel flush aria-label={k('title')}>
          <TableSkeleton columns={8} label={k('title')} />
        </Panel>
      ) : opened ? (
        <VerificationDetail
          key={opened.id}
          detail={opened}
          source={source}
          onBack={() => setOpened(null)}
          onChanged={changed}
          onError={(error) => setNotice({ tone: 'danger', text: message(error) })}
        />
      ) : view.vesselId === null ? (
        <Panel>
          <EmptyState title={k('empty.title')} description={k('empty.noVessel')} />
        </Panel>
      ) : (
        <VerificationList
          view={view}
          scope={source.scope}
          onOpen={(id) => void open(id)}
          onVessel={setVesselId}
        />
      )}
      {source.create && view ? (
        <CreateDialog
          opened={creating}
          candidates={view.candidates}
          tzOffsetMin={view.tzOffsetMin}
          onClose={() => setCreating(false)}
          onConfirm={async (input) => {
            try {
              const created = await (source.create as NonNullable<typeof source.create>)(input);
              setCreating(false);
              changed(created, k('notice.created', { number: created.number }));
              return null;
            } catch (error) {
              return errorCode(error);
            }
          }}
        />
      ) : null}
    </div>
  );
}

/** Liste: özet kartları, süzgeç ve doğrulamalar tablosu; ofiste gemi seçimi. */
export function VerificationList({
  view,
  scope,
  onOpen,
  onVessel,
}: {
  view: RaVerificationListView;
  scope: 'ship' | 'web';
  onOpen: (id: string) => void;
  onVessel?: (vesselId: string) => void;
}) {
  const { k } = useK();
  const [filter, setFilter] = useState<VerificationListFilter>(EMPTY_VERIFICATION_FILTER);
  const [page, setPage] = useState(1);
  const items = useMemo(
    () => filterVerifications(view.verifications, filter),
    [view.verifications, filter],
  );
  const stats = verificationStats(view.verifications);
  const pages = Math.max(1, Math.ceil(items.length / PAGE_SIZE));
  const current = Math.min(page, pages);
  const visible = items.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);
  return (
    <>
      <div className={master.stats}>
        <StatTile
          icon={<IconChecklist size={22} />}
          label={k('stats.open')}
          value={stats.open}
          variant="plain"
        />
        <StatTile
          icon={<IconHourglass size={22} />}
          label={k('stats.pending')}
          value={stats.pending}
          tone={stats.pending > 0 ? 'warning' : 'default'}
          variant="plain"
        />
        <StatTile
          icon={<IconHandStop size={22} />}
          label={k('stats.blocked')}
          value={stats.blocked}
          tone={stats.blocked > 0 ? 'danger' : 'default'}
          variant="plain"
        />
        <StatTile
          icon={<IconShieldCheck size={22} />}
          label={k('stats.active')}
          value={stats.active}
          variant="plain"
        />
      </div>
      <Panel flush aria-label={k('title')}>
        <div className={master.toolbar}>
          {scope === 'web' && onVessel ? (
            <Select
              className={master.filter}
              label={k('vessel')}
              data={view.vessels.map((v) => ({ value: v.id, label: v.name }))}
              value={view.vesselId}
              allowDeselect={false}
              onChange={(value) => {
                if (!value) return;
                onVessel(value);
                setPage(1);
              }}
            />
          ) : null}
          <TextInput
            className={master.search}
            leftSection={<IconSearch size={16} aria-hidden />}
            placeholder={k('search')}
            aria-label={k('search')}
            value={filter.query}
            onChange={(event) => {
              setFilter({ ...filter, query: event.currentTarget.value });
              setPage(1);
            }}
          />
          <Select
            className={master.filter}
            label={k('filter.state')}
            data={VERIFICATION_FILTERS.map((s) => ({ value: s, label: k(`filter.states.${s}`) }))}
            value={filter.state}
            allowDeselect={false}
            onChange={(value) => {
              setFilter({ ...filter, state: (value ?? 'all') as VerificationListFilter['state'] });
              setPage(1);
            }}
          />
        </div>
        {view.verifications.length === 0 ? (
          <EmptyState
            title={k('empty.title')}
            description={
              scope === 'ship'
                ? view.candidates.length === 0
                  ? k('empty.noCandidates')
                  : k('empty.ship')
                : k('empty.office')
            }
          />
        ) : items.length === 0 ? (
          <NoMatch
            title={k('empty.noMatch')}
            onClear={() => setFilter(EMPTY_VERIFICATION_FILTER)}
          />
        ) : (
          <>
            <section
              className={table.tableWrap}
              aria-label={k('tableLabel')}
              // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
              tabIndex={0}
            >
              <table className={`${table.table} ${purchasing.table}`}>
                <thead>
                  <tr>
                    <th scope="col">{k('columns.number')}</th>
                    <th scope="col">{k('columns.operation')}</th>
                    <th scope="col">{k('columns.assessment')}</th>
                    <th scope="col">{k('columns.planned')}</th>
                    <th scope="col">{k('columns.checks')}</th>
                    <th scope="col">{k('columns.residual')}</th>
                    <th scope="col">{k('columns.status')}</th>
                    <th scope="col">{k('columns.authorization')}</th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map((v) => (
                    <VerificationRow key={v.id} v={v} onOpen={onOpen} />
                  ))}
                </tbody>
              </table>
            </section>
            <TablePager
              page={current}
              pages={pages}
              summary={k('showing', {
                from: (current - 1) * PAGE_SIZE + 1,
                to: Math.min(current * PAGE_SIZE, items.length),
                total: items.length,
              })}
              label={k('pagination')}
              onPage={setPage}
            />
          </>
        )}
      </Panel>
    </>
  );
}

function VerificationRow({
  v,
  onOpen,
}: {
  v: RaVerificationSummary;
  onOpen: (id: string) => void;
}) {
  const { k } = useK();
  return (
    <tr className={classes.row} data-attention={v.status === 'pending_authorization' || undefined}>
      <td>
        <button
          type="button"
          className={master.codeButton}
          aria-label={k('open', { number: v.number })}
          onClick={() => onOpen(v.id)}
        >
          {v.number}
        </button>
      </td>
      <td className={master.wrapCell}>
        <span className={classes.stack}>
          <span>{v.operation}</span>
          {v.location ? <span className={master.sub}>{v.location}</span> : null}
        </span>
      </td>
      <td className={master.wrapCell}>
        <span className={classes.stack}>
          <span>
            {v.code} · {v.revision}
          </span>
          <span className={master.sub}>{v.title}</span>
        </span>
      </td>
      <td>
        <span className={classes.stack}>
          <span className={classes.nowrap}>{formatDateTime(v.plannedStart, v.tzOffsetMin)}</span>
          {v.plannedEnd ? (
            <span className={`${master.sub} ${classes.nowrap}`}>
              {formatDateTime(v.plannedEnd, v.tzOffsetMin)}
            </span>
          ) : null}
        </span>
      </td>
      <td>
        <span className={classes.stack}>
          <span className={classes.nowrap}>
            {k('checksValue', { answered: v.answered, total: v.total })}
          </span>
          {v.failed > 0 ? (
            <StatusBadge tone="danger" withIcon={false}>
              {k('failedCount', { count: v.failed })}
            </StatusBadge>
          ) : null}
        </span>
      </td>
      <td>
        {v.risk.residual ? (
          <LevelBadge level={v.risk.residual.level} score={v.risk.residual.score} />
        ) : (
          k('none')
        )}
      </td>
      <td>
        <StatusBadge tone={verificationTone(v.status)} withIcon={false}>
          {k(`statuses.${v.status}`)}
        </StatusBadge>
      </td>
      <td>
        {v.authorizedByName && v.validUntil ? (
          <span className={classes.stack}>
            <span>{v.authorizedByName}</span>
            <span className={master.sub}>
              {k('until', { time: formatDateTime(v.validUntil, v.tzOffsetMin) })}
            </span>
          </span>
        ) : (
          k('none')
        )}
      </td>
    </tr>
  );
}

/** Ayrıntı: başlık ve işlemler, uyarılar, operasyon, kontrol listesi, brifing ve geçmiş. */
export function VerificationDetail({
  detail,
  source,
  onBack,
  onChanged,
  onError,
}: {
  detail: RaVerificationDetail;
  source: RaVerificationSource;
  onBack: () => void;
  onChanged: (next: RaVerificationDetail, notice: string | null) => void;
  onError: (error: unknown) => void;
}) {
  const { k, t } = useK();
  const [printing, setPrinting] = useState(false);
  /** Doğrulama kaydının PDF'i (ADR 0095 §2): cevaplar, yetki ve olay izi. */
  const print = async () => {
    setPrinting(true);
    try {
      const brand = (await source.brand?.().catch(() => null)) ?? null;
      const tr = (key: string, options?: Record<string, unknown>) => String(t(key, options ?? {}));
      const definition = raVerificationPdf(tr, {
        detail,
        vessel: { name: detail.vesselName ?? '' },
        brand,
        printedAt: new Date().toISOString(),
      });
      const output = source.pdf ?? browserPdfOutput();
      await output.print(
        raPdfFileName(detail.number, t('riskDashboard.pdf.verification.title')),
        () => renderPdf(definition, { print: true }),
      );
    } catch (error) {
      const key = pdfErrorKey(errorCode(error));
      onError(
        Object.assign(new Error(key), {
          code: key === 'restHours.pdf.error' ? 'PDF_FAILED' : key,
        }),
      );
    } finally {
      setPrinting(false);
    }
  };
  const [drafts, setDrafts] = useState<Record<string, AnswerDraft>>(() =>
    Object.fromEntries(detail.items.map((i) => [i.key, draftOf(i)])),
  );
  const [saving, setSaving] = useState(false);
  const [action, setAction] = useState<(typeof ACTIONS)[number] | null>(null);
  const [editing, setEditing] = useState(false);
  const [validUntil, setValidUntil] = useState(() =>
    zonedInput(detail.defaultValidUntil, detail.tzOffsetMin),
  );
  const answerable = detail.editable && detail.can.edit && source.answer !== undefined;
  const { answers, invalid } = changedAnswers(detail.items, drafts);
  const offset = detail.tzOffsetMin;

  const save = async () => {
    if (!source.answer || answers.length === 0 || invalid.length > 0) return;
    setSaving(true);
    try {
      const next = await source.answer(detail.id, answers);
      setDrafts(Object.fromEntries(next.items.map((i) => [i.key, draftOf(i)])));
      onChanged(next, k('notice.saved'));
    } catch (error) {
      onError(error);
    } finally {
      setSaving(false);
    }
  };

  const buttons = ACTIONS.filter(
    (a) => source.event !== undefined && detail.can[a.can] === true && a.kind !== 'stopped',
  );
  const stop = ACTIONS.find((a) => a.kind === 'stopped');
  return (
    <>
      <Panel flush aria-label={`${detail.number} · ${detail.operation}`}>
        <div className={library.head}>
          <div className={library.headTitle}>
            <Button
              variant="subtle"
              size="compact-sm"
              leftSection={<IconArrowLeft size={14} />}
              onClick={onBack}
              style={{ alignSelf: 'flex-start' }}
            >
              {k('detail.back')}
            </Button>
            <h2>
              {detail.number} · {detail.operation}
            </h2>
            <span className={library.meta}>
              <StatusBadge tone={verificationTone(detail.status)} withIcon={false}>
                {k(`statuses.${detail.status}`)}
              </StatusBadge>
              <span>{k('detail.round', { round: detail.round })}</span>
              <span>
                {k('detail.version', {
                  code: detail.code,
                  revision: detail.revision,
                  title: detail.title,
                })}
              </span>
              {source.scope === 'web' ? <span>{k('detail.readOnly')}</span> : null}
            </span>
          </div>
          <div className={classes.actions}>
            <Button
              variant="default"
              leftSection={<IconPrinter size={16} />}
              loading={printing}
              onClick={() => void print()}
            >
              {t('riskDashboard.pdf.button')}
            </Button>
            {detail.can.edit && source.update ? (
              <Button
                variant="default"
                leftSection={<IconPencil size={16} />}
                onClick={() => setEditing(true)}
              >
                {k('actions.edit')}
              </Button>
            ) : null}
            {buttons.map((a) => (
              <Button
                key={a.kind}
                variant={
                  a.kind === 'submitted' || a.kind === 'authorized' || a.kind === 'started'
                    ? 'filled'
                    : 'default'
                }
                {...(a.danger ? { color: 'red' } : {})}
                disabled={
                  (a.kind === 'submitted' && (answers.length > 0 || detail.next === null)) ||
                  (a.kind === 'authorized' &&
                    (detail.risk.blocked || (detail.risk.masterRequired && !detail.can.isMaster)))
                }
                leftSection={a.kind === 'started' ? <IconPlayerPlay size={16} /> : undefined}
                onClick={() => setAction(a)}
              >
                {k(`actions.${a.key}`)}
              </Button>
            ))}
            {stop && detail.can.stop && source.event ? (
              <Button
                color="red"
                leftSection={<IconHandStop size={16} />}
                onClick={() => setAction(stop)}
              >
                {k('actions.stop')}
              </Button>
            ) : null}
          </div>
        </div>
        <div className={classes.banners}>
          {detail.status === 'pending_authorization' ? (
            <Banner tone="info">{k('detail.notAuthorization')}</Banner>
          ) : null}
          {detail.editable && detail.next === 'on_hold' ? (
            <Banner tone="warning">{k('detail.willHold')}</Banner>
          ) : null}
          {detail.editable && detail.next === 'escalated' ? (
            <Banner tone="danger">{k('detail.willEscalate')}</Banner>
          ) : null}
          {/* İade ya da ret (ADR 0116 Ek): kim, ne zaman, gerekçe; eski sunucuda genel bant */}
          {detail.returnNotice ? (
            <ReturnedBanner
              notice={detail.returnNotice}
              offsetMin={detail.tzOffsetMin}
              hint={detail.editable}
            />
          ) : detail.returned ? (
            <Banner tone="warning">{k('detail.returned')}</Banner>
          ) : null}
          {detail.status === 'pending_authorization' &&
          (detail.findings ?? []).some((f) => openFinding(f.status) && f.blocking) ? (
            <Banner tone="danger">{k('detail.findingsBlocking')}</Banner>
          ) : null}
          {detail.status === 'completed' &&
          (detail.findings ?? []).some((f) => openFinding(f.status)) ? (
            <Banner tone="warning">{k('detail.findingsOpen')}</Banner>
          ) : null}
          {detail.expired ? <Banner tone="danger">{k('detail.expired')}</Banner> : null}
          {detail.risk.blocked ? <Banner tone="danger">{k('detail.critical')}</Banner> : null}
          {detail.risk.masterRequired &&
          ['pending_authorization', 'verification_completed'].includes(detail.status) ? (
            <Banner tone="warning">{k('detail.masterRequired')}</Banner>
          ) : null}
          {detail.status === 'authorized' || detail.status === 'in_progress' ? (
            <Banner tone="success">
              {k('detail.authorized', {
                name: detail.authorizedByName ?? k('none'),
                time: shipTime(detail.validUntil, offset),
              })}
            </Banner>
          ) : null}
        </div>
        <div className={library.body}>
          <section className={library.card} aria-label={k('detail.operationCard')}>
            <div className={library.cardHead}>
              <span>{k('detail.operationCard')}</span>
            </div>
            <div className={library.cardGrid}>
              <Field label={k('detail.location')} value={detail.location ?? k('none')} />
              <Field
                label={k('detail.plannedStart')}
                value={shipTime(detail.plannedStart, offset)}
              />
              <Field label={k('detail.plannedEnd')} value={shipTime(detail.plannedEnd, offset)} />
              <Field
                label={k('detail.residual')}
                value={
                  detail.risk.residual ? (
                    <LevelBadge
                      level={detail.risk.residual.level}
                      score={detail.risk.residual.score}
                    />
                  ) : (
                    k('none')
                  )
                }
              />
              <Field
                label={k('detail.checklist')}
                value={k('detail.checklistVersion', {
                  common: detail.checklist.common,
                  revision: detail.checklist.revision,
                })}
              />
              <Field
                label={k('detail.createdLabel')}
                value={k('detail.created', {
                  name: detail.createdByName ?? k('none'),
                  time: shipTime(detail.createdAt, offset),
                })}
              />
            </div>
          </section>

          <section className={library.card} aria-label={k('detail.checklist')}>
            <div className={library.cardHead}>
              <span>{k('detail.checklistRound', { round: detail.round })}</span>
              <span className={library.meta}>
                {k('detail.progress', { answered: detail.answered, total: detail.total })}
              </span>
            </div>
            <div className={library.cardBody}>
              {groupItems(detail.items).map((group) => (
                <div key={group.source} className={classes.group}>
                  <h3 className={classes.groupTitle}>{k(`groups.${group.source}`)}</h3>
                  <ol className={classes.items}>
                    {group.items.map((item) => (
                      <ChecklistItem
                        key={item.key}
                        item={item}
                        draft={drafts[item.key] ?? draftOf(item)}
                        editable={answerable}
                        invalid={invalid.includes(item.key)}
                        failed={detail.failedKeys.includes(item.key)}
                        onDraft={(next) => setDrafts({ ...drafts, [item.key]: next })}
                      />
                    ))}
                  </ol>
                </div>
              ))}
              {answerable ? (
                <div className={classes.saveRow}>
                  {invalid.length > 0 ? (
                    <span className={classes.invalid} role="alert">
                      {k('detail.invalid')}
                    </span>
                  ) : answers.length > 0 ? (
                    <span className={library.meta}>
                      {k('detail.unsaved', { count: answers.length })}
                    </span>
                  ) : null}
                  <Button
                    loading={saving}
                    disabled={answers.length === 0 || invalid.length > 0}
                    onClick={() => void save()}
                  >
                    {k('detail.save')}
                  </Button>
                </div>
              ) : null}
            </div>
          </section>

          <FindingsCard detail={detail} scope={source.scope} />

          <section className={library.card} aria-label={k('detail.briefing')}>
            <div className={library.cardHead}>
              <span>
                <IconUsersGroup size={16} aria-hidden /> {k('detail.briefing')}
              </span>
            </div>
            <div className={library.cardBody}>
              <p className={classes.muted}>{k('detail.briefingNote')}</p>
              {detail.briefed.length === 0 ? (
                <span className={classes.muted}>{k('detail.noBriefing')}</span>
              ) : (
                <ul className={classes.briefed}>
                  {detail.briefed.map((b) => (
                    <li key={b.userId}>
                      {k('detail.briefedBy', {
                        name: b.name ?? k('none'),
                        time: shipTime(b.at, offset),
                      })}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </section>

          <section className={library.card} aria-label={k('detail.history')}>
            <div className={library.cardHead}>
              <span>{k('detail.history')}</span>
            </div>
            {detail.events.length === 0 ? (
              <div className={library.cardBody}>
                <span className={classes.muted}>{k('none')}</span>
              </div>
            ) : (
              <ol className={classes.timeline}>
                {detail.events.map((e) => (
                  <li key={e.id}>
                    <span className={classes.when}>{shipTime(e.at, e.tzOffsetMin)}</span>
                    <span className={classes.what}>
                      <span>
                        <strong>{k(`events.${e.kind}`)}</strong>
                        {' · '}
                        {e.byName ?? k('none')}
                        {e.roleName ? ` (${e.roleName})` : ''}
                      </span>
                      {e.outcome ? (
                        <span className={classes.detailLine}>
                          {k('detail.outcome', { status: k(`statuses.${e.outcome}`) })}
                        </span>
                      ) : null}
                      {e.reason ? <span className={classes.detailLine}>{e.reason}</span> : null}
                      {e.kind === 'authorized' ? (
                        <span className={classes.detailLine}>
                          {k('validUntil', { time: shipTime(e.validUntil, e.tzOffsetMin) })}
                          {e.residualBand
                            ? ` · ${k('detail.residualBand', { band: e.residualBand.replace('_', ' ') })}`
                            : ''}
                          {e.masterAcceptance ? ` · ${k('detail.masterAcceptance')}` : ''}
                          {e.sameActor ? ` · ${k('detail.sameActor')}` : ''}
                        </span>
                      ) : null}
                    </span>
                  </li>
                ))}
              </ol>
            )}
          </section>
        </div>
      </Panel>
      {action && source.event ? (
        <ActionDialog
          opened
          title={k(`dialogs.${action.key}.title`, { number: detail.number })}
          body={k(`dialogs.${action.key}.body`)}
          reason={action.reason}
          password={action.password}
          reauthKind="ra_operation"
          confirmLabel={k(`actions.${action.key}`)}
          {...(action.danger ? { danger: true } : {})}
          messageOf={(code) =>
            k(`errors.${code}`, { defaultValue: '' }) || `${k('errors.GENERIC')} (${code})`
          }
          note={
            action.kind === 'authorized'
              ? [
                  detail.risk.masterRequired ? k('dialogs.authorize.masterNote') : null,
                  detail.can.selfSubmitted ? k('dialogs.authorize.sameActorNote') : null,
                ]
                  .filter(Boolean)
                  .join(' ') || null
              : null
          }
          extra={
            action.kind === 'authorized' ? (
              <DateTimeInput
                label={k('dialogs.authorize.validUntil')}
                description={k('dialogs.authorize.validityNote')}
                value={validUntil}
                onChange={setValidUntil}
                required
              />
            ) : null
          }
          extraBlocked={action.kind === 'authorized' && fromZonedInput(validUntil, offset) === null}
          onClose={() => setAction(null)}
          onConfirm={async (input) => {
            try {
              const valid =
                action.kind === 'authorized' ? fromZonedInput(validUntil, offset) : null;
              const next = await (source.event as NonNullable<typeof source.event>)(detail.id, {
                kind: action.kind,
                ...(['submitted', 'authorized', 'returned', 'rejected'].includes(action.kind)
                  ? { contentHash: detail.contentHash }
                  : {}),
                ...(input.reason ? { reason: input.reason } : {}),
                ...(valid ? { validUntil: valid } : {}),
                ...(input.password ? { password: input.password } : {}),
              });
              setAction(null);
              onChanged(
                next,
                k('notice.event', { number: next.number, event: k(`events.${action.kind}`) }),
              );
              return null;
            } catch (error) {
              return errorCode(error);
            }
          }}
        />
      ) : null}
      {editing && source.update ? (
        <HeaderDialog
          title={k('edit.title')}
          confirmLabel={k('edit.confirm')}
          initial={detail}
          tzOffsetMin={offset}
          onClose={() => setEditing(false)}
          onConfirm={async (header) => {
            try {
              const next = await (source.update as NonNullable<typeof source.update>)(
                detail.id,
                detail.rowVersion,
                header,
              );
              setEditing(false);
              onChanged(next, null);
              return null;
            } catch (error) {
              return errorCode(error);
            }
          }}
        />
      ) : null}
    </>
  );
}

const openFinding = (status: string) => status !== 'closed' && status !== 'cancelled';

/** Doğrulamanın bulguları (K6; ADR 0094): bulguya git ya da gemide yeni bulgu bildir. */
function FindingsCard({ detail, scope }: { detail: RaVerificationDetail; scope: 'ship' | 'web' }) {
  const { k, t } = useK();
  const shell = useShell();
  const findings = detail.findings ?? [];
  const canReport =
    scope === 'ship' &&
    detail.can.create &&
    detail.status !== 'closed' &&
    detail.status !== 'cancelled';
  return (
    <section className={library.card} aria-label={k('detail.findingsCard')}>
      <div className={library.cardHead}>
        <span>{k('detail.findingsCard')}</span>
        {canReport ? (
          <Button
            size="compact-sm"
            variant="default"
            leftSection={<IconPlus size={14} />}
            onClick={() =>
              shell?.navigate(raFindingsPath({ view: 'new', verificationId: detail.id }))
            }
          >
            {k('detail.reportFinding')}
          </Button>
        ) : null}
      </div>
      {findings.length === 0 ? (
        <div className={library.cardBody}>
          <span className={classes.muted}>{k('detail.noFindings')}</span>
        </div>
      ) : (
        <ul className={classes.findings}>
          {findings.map((f) => (
            <li key={f.id}>
              <button
                type="button"
                className={master.codeButton}
                onClick={() => shell?.navigate(raFindingsPath({ view: 'detail', id: f.id }))}
              >
                {f.number}
              </button>
              <span>{t(`riskFindings.types.${f.type}`)}</span>
              <StatusBadge tone={findingTone(f.status)} withIcon={false}>
                {t(`riskFindings.statuses.${f.status}`)}
              </StatusBadge>
              {openFinding(f.status) && f.blocking ? (
                <StatusBadge tone="danger" withIcon={false}>
                  {t('riskFindings.blocking')}
                </StatusBadge>
              ) : null}
              <span className={classes.findingText}>{f.description}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function Banner({
  tone,
  children,
}: {
  tone: 'info' | 'warning' | 'danger' | 'success';
  children: ReactNode;
}) {
  return (
    <p className={classes.banner} data-tone={tone} role={tone === 'danger' ? 'alert' : 'status'}>
      {tone === 'danger' || tone === 'warning' ? <IconAlertTriangle size={16} aria-hidden /> : null}
      <span>{children}</span>
    </p>
  );
}

function Field({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div>
      <div className={classes.fieldLabel}>{label}</div>
      <div className={classes.fieldValue}>{value}</div>
    </div>
  );
}

const ANSWER_TONE = { yes: 'success', no: 'danger', na: 'neutral' } as const;

/** Maddenin satırı: metin, ipucu, Yes/No/N/A, gerekçe ve kanıt notu; önceki cevaplar. */
function ChecklistItem({
  item,
  draft,
  editable,
  invalid,
  failed,
  onDraft,
}: {
  item: RaVerificationItem;
  draft: AnswerDraft;
  editable: boolean;
  invalid: boolean;
  failed: boolean;
  onDraft: (next: AnswerDraft) => void;
}) {
  const { k } = useK();
  const text =
    item.source === 'common' ? k(`common.${item.key.slice('common:'.length)}`) : (item.text ?? '');
  const hint =
    item.source === 'common' ? k(`hints.${item.key.slice('common:'.length)}`) : item.context;
  const earlier = item.history.filter((h) => h.id !== item.current?.id);
  return (
    <li className={classes.item} data-failed={failed || undefined}>
      <div className={classes.itemText}>
        <span className={classes.itemTitle}>{text}</span>
        {hint ? <span className={classes.muted}>{hint}</span> : null}
      </div>
      {editable ? (
        <div className={classes.itemInputs}>
          <Radio.Group
            aria-label={k('answers.label', { item: text })}
            value={draft.answer ?? ''}
            onChange={(value) => onDraft({ ...draft, answer: value as AnswerDraft['answer'] })}
          >
            <div className={classes.radios}>
              <Radio value="yes" label={k('answers.yes')} />
              <Radio value="no" label={k('answers.no')} />
              {item.naAllowed ? <Radio value="na" label={k('answers.na')} /> : null}
            </div>
          </Radio.Group>
          <Textarea
            label={
              draft.answer && draft.answer !== 'yes'
                ? k('answers.commentRequired')
                : k('answers.comment')
            }
            required={draft.answer !== null && draft.answer !== 'yes'}
            error={invalid ? k('detail.invalid') : undefined}
            value={draft.comment}
            maxLength={1000}
            autosize
            minRows={1}
            onChange={(e) => onDraft({ ...draft, comment: e.currentTarget.value })}
          />
          <TextInput
            label={k('answers.evidence')}
            value={draft.evidence}
            maxLength={500}
            onChange={(e) => onDraft({ ...draft, evidence: e.currentTarget.value })}
          />
        </div>
      ) : (
        <div className={classes.itemAnswer}>
          {item.current ? (
            <>
              <StatusBadge tone={ANSWER_TONE[item.current.answer]} withIcon={false}>
                {k(`answers.${item.current.answer}`)}
              </StatusBadge>
              {item.current.comment ? <span>{item.current.comment}</span> : null}
              {item.current.evidence ? (
                <span className={classes.muted}>{item.current.evidence}</span>
              ) : null}
              <span className={classes.muted}>
                {item.current.byName ?? k('none')} ·{' '}
                {shipTime(item.current.at, item.current.tzOffsetMin)}
              </span>
            </>
          ) : (
            <span className={classes.muted}>{k('none')}</span>
          )}
        </div>
      )}
      {earlier.length > 0 ? (
        <details className={classes.history}>
          <summary>{k('answers.history', { count: earlier.length })}</summary>
          <ul>
            {earlier.map((h) => (
              <li key={h.id}>
                {k('answers.historyItem', {
                  round: h.round,
                  answer: k(`answers.${h.answer}`),
                  name: h.byName ?? k('none'),
                  time: shipTime(h.at, h.tzOffsetMin),
                })}
                {h.comment ? ` — ${h.comment}` : ''}
              </li>
            ))}
          </ul>
        </details>
      ) : null}
    </li>
  );
}

/** Planlanan operasyonun alanları (yeni doğrulama ve düzeltme). */
function HeaderFields({
  value,
  tzOffsetMin,
  onChange,
}: {
  value: { operation: string; location: string; start: string; end: string };
  tzOffsetMin: number;
  onChange: (next: { operation: string; location: string; start: string; end: string }) => void;
}) {
  const { k } = useK();
  return (
    <>
      <TextInput
        label={k('create.operation')}
        placeholder={k('create.operationPlaceholder')}
        required
        maxLength={300}
        value={value.operation}
        onChange={(e) => onChange({ ...value, operation: e.currentTarget.value })}
      />
      <TextInput
        label={k('create.location')}
        maxLength={200}
        value={value.location}
        onChange={(e) => onChange({ ...value, location: e.currentTarget.value })}
      />
      <div className={classes.pair}>
        <DateTimeInput
          label={k('create.plannedStart')}
          value={value.start}
          onChange={(start) => onChange({ ...value, start })}
          clearable
        />
        <DateTimeInput
          label={k('create.plannedEnd')}
          value={value.end}
          onChange={(end) => onChange({ ...value, end })}
          clearable
        />
      </div>
      <p className={classes.muted}>{k('create.timesNote', { offset: offsetLabel(tzOffsetMin) })}</p>
    </>
  );
}

function headerOf(
  value: { operation: string; location: string; start: string; end: string },
  tzOffsetMin: number,
): RaVerificationHeaderInput {
  return {
    operation: value.operation,
    location: value.location.trim() === '' ? null : value.location,
    plannedStart: value.start ? fromZonedInput(value.start, tzOffsetMin) : null,
    plannedEnd: value.end ? fromZonedInput(value.end, tzOffsetMin) : null,
  };
}

function HeaderDialog({
  title,
  confirmLabel,
  initial,
  tzOffsetMin,
  onClose,
  onConfirm,
}: {
  title: string;
  confirmLabel: string;
  initial: RaVerificationDetail;
  tzOffsetMin: number;
  onClose: () => void;
  onConfirm: (header: RaVerificationHeaderInput) => Promise<string | null>;
}) {
  const { t } = useK();
  const message = useVerificationError();
  const [value, setValue] = useState({
    operation: initial.operation,
    location: initial.location ?? '',
    start: initial.plannedStart ? zonedInput(initial.plannedStart, tzOffsetMin) : '',
    end: initial.plannedEnd ? zonedInput(initial.plannedEnd, tzOffsetMin) : '',
  });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  return (
    <Modal
      opened
      onClose={onClose}
      title={title}
      closeButtonProps={{ 'aria-label': t('risk.dialog.cancel') }}
    >
      <form
        className={risk.dialogBody}
        onSubmit={async (event) => {
          event.preventDefault();
          setBusy(true);
          const code = await onConfirm(headerOf(value, tzOffsetMin));
          setBusy(false);
          setError(code);
        }}
      >
        <HeaderFields value={value} tzOffsetMin={tzOffsetMin} onChange={setValue} />
        {error ? (
          <p className={risk.error} role="alert">
            {message(error)}
          </p>
        ) : null}
        <div className={risk.formActions}>
          <Button type="submit" loading={busy} disabled={value.operation.trim() === ''}>
            {confirmLabel}
          </Button>
          <Button variant="default" onClick={onClose}>
            {t('risk.dialog.cancel')}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

/** Yeni doğrulama: atanmış güncel sürüm seçilir (FD §9), planlanan operasyon yazılır. */
function CreateDialog({
  opened,
  candidates,
  tzOffsetMin,
  onClose,
  onConfirm,
}: {
  opened: boolean;
  candidates: readonly RaVerificationCandidate[];
  tzOffsetMin: number;
  onClose: () => void;
  onConfirm: (input: RaVerificationHeaderInput & { versionId: string }) => Promise<string | null>;
}) {
  const { t, k } = useK();
  const message = useVerificationError();
  const [versionId, setVersionId] = useState<string | null>(null);
  const [value, setValue] = useState({ operation: '', location: '', start: '', end: '' });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const chosen = candidates.find((c) => c.versionId === versionId);
  const close = () => {
    setVersionId(null);
    setValue({ operation: '', location: '', start: '', end: '' });
    setError(null);
    onClose();
  };
  return (
    <Modal
      opened={opened}
      onClose={close}
      title={k('create.title')}
      size="lg"
      closeButtonProps={{ 'aria-label': t('risk.dialog.cancel') }}
    >
      <form
        className={risk.dialogBody}
        onSubmit={async (event) => {
          event.preventDefault();
          if (!chosen || chosen.risk.blocked) return;
          setBusy(true);
          const code = await onConfirm({
            versionId: chosen.versionId,
            ...headerOf(value, tzOffsetMin),
          });
          setBusy(false);
          if (code) setError(code);
          else close();
        }}
      >
        {candidates.length === 0 ? (
          <p className={classes.muted}>{k('empty.noCandidates')}</p>
        ) : null}
        <Select
          label={k('create.version')}
          placeholder={k('create.versionPlaceholder')}
          required
          searchable
          data={candidates.map((c) => ({
            value: c.versionId,
            label: `${c.code} · ${c.revision} · ${c.title}`,
            disabled: c.risk.blocked,
          }))}
          value={versionId}
          onChange={setVersionId}
        />
        {chosen ? (
          <div className={classes.candidate}>
            {chosen.risk.residual ? (
              <LevelBadge level={chosen.risk.residual.level} score={chosen.risk.residual.score} />
            ) : null}
            <span className={classes.muted}>
              {k('create.items', {
                count: chosen.checklistItems,
                activity: chosen.activityChecks,
                controls: chosen.controlChecks,
              })}
            </span>
            {chosen.risk.blocked ? (
              <span className={classes.invalid}>{k('create.blocked')}</span>
            ) : chosen.risk.masterRequired ? (
              <span>{k('create.masterRequired')}</span>
            ) : null}
          </div>
        ) : null}
        <HeaderFields value={value} tzOffsetMin={tzOffsetMin} onChange={setValue} />
        {error ? (
          <p className={risk.error} role="alert">
            {message(error)}
          </p>
        ) : null}
        <div className={risk.formActions}>
          <Button
            type="submit"
            loading={busy}
            disabled={!chosen || chosen.risk.blocked || value.operation.trim() === ''}
          >
            {k('create.confirm')}
          </Button>
          <Button variant="default" onClick={close}>
            {t('risk.dialog.cancel')}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

/** Operational Verification'ın yolu. */
export const RA_VERIFICATION_PATH = '/compliance/risk-assessment/verification';
